import _ from "lodash";
import { Form, FormikHelpers, FormikProps } from "formik";
import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { useDispatch } from "react-redux";
import { useHistory, useParams } from "react-router-dom";

import { AcceptedAgeRange } from "@/portals/(shared)/components/AcceptedAgeRange";
import { admin_cse, provider_case_manager, provider_peer, providerRoute } from "@/config/roles";
import Button from "@/components/UI/Button";
import { Button2 } from "@/ui2/atoms/Button";
import {
  CARE_MANAGER_VALIDATION_SCHEMA,
  VALIDATION_SCHEMA,
} from "@/portals/(shared)/constants/provider-profile-basic-schema";
import { CustomForm } from "@/component-library/CustomForm";
import { DashboardCard2 } from "@/components/layout/DashboardCard2";
import dayjs from "@/lib/helpers/dayjs";
import FormikInput from "@/components/UI/FormikInput";
import FormikMobileInput from "@/components/UI/formikMobileInput";
import { getAutomationDataAttr } from "@/utils/automationUtils";
import { isAuthorized, isUserAdmin, isUserPCP } from "@/utils/authToken";
import { MyProfile } from "@/portals/(shared)/(profile)/MyProfile";
import { ProfileField } from "@/portals/(shared)/(profile)/components/ProfileField";
import { ProfileSidebar } from "@/portals/(shared)/(profile)/components/ProfileSidebar";
import { ProviderEnum } from "@/core/constants/provider";
import type { ProviderProfile as ProviderProfileData, UUID } from "@/core/types";
import { providerRequests } from "@/api/(requests)/provider";
import type { SelectOption } from "@/components/UI2/types";
import { setDynamicRouteConfiguration } from "@/redux/actions/navbar";
import { SubmitControls } from "@/components/Common/SubmitControls";
import { useRawInsuranceList } from "@/features/insurance/api/queries/useRawInsuranceList";

import { AcceptedInsuranceCard } from "./components/AcceptedInsuranceCard";
import { AcceptNewPatientsCard } from "./components/AcceptNewPatientsCard";
import { adaptProviderInsuranceOptions } from "./helpers/adapters";
import { CalendarPreferencesCard } from "./components/CalendarPreferencesCard";
import { PROVIDER_BASIC_SCHEMA } from "./constants/schema";
import { RequireCosignatureGate } from "./(cosignature)/RequireCosignature";
import { StatesToPractice } from "./components/StatesToPractice";
import { UniversalAccessManagementCard } from "./components/UniversalAccessManagementCard";
import { useProviderProfile } from "./api/queries/useProviderProfile";
import { useUpdateProviderProfile } from "./api/mutations/useUpdateProviderProfile";

import "./ProviderProfile.styles.scss";

type AgeRange = ProviderProfileData["accepted_age_range"][number];
type InsuranceCarrier = ProviderProfileData["accepted_insurance"][number];
type StateToPractice = ProviderProfileData["states_to_practice"][number];
type SchemaItem = (typeof PROVIDER_BASIC_SCHEMA)[number];

// react-select v3 ships without bundled types; define locally what we need
type SelectActionMeta =
  | { action: "clear" }
  | { action: "remove-value"; removedValue: SelectOption<InsuranceCarrier> }
  | { action: "select-option"; option?: SelectOption<InsuranceCarrier> };

export function ProviderProfile() {
  const dispatch = useDispatch();
  const history = useHistory();
  const { providerId: routeProviderId } = useParams<{ providerId?: string }>();

  const providerId = routeProviderId ?? null;
  const validationSchema = isUserPCP() ? CARE_MANAGER_VALIDATION_SCHEMA : VALIDATION_SCHEMA;

  const { data: profileData, isLoading } = useProviderProfile(
    (providerId ?? undefined) as UUID | undefined,
  );
  const { mutateAsync } = useUpdateProviderProfile(providerId ?? undefined);

  const [acceptedAgeRanges, setAcceptedAgeRanges] = useState<AgeRange[]>([]);
  const [acceptNewPatients, setAcceptNewPatients] = useState<boolean>(true);
  const [editBasic, setEditBasic] = useState<boolean>(false);
  const [hasScrolledOnLoad, setHasScrolledOnLoad] = useState<boolean>(false);
  const [showInsurancePopUp, setShowInsurancePopUp] = useState<boolean>(false);
  const [stateModal, setStateModal] = useState<boolean | null>(null);
  const [tempAcceptedInsuranceList, setTempAcceptedInsuranceList] = useState<
    SelectOption<InsuranceCarrier>[]
  >([]);
  const [acceptedInsuranceList, setAcceptedInsuranceList] = useState<
    SelectOption<InsuranceCarrier>[]
  >([]);

  // Seed optimistic local state from server data
  useEffect(() => {
    if (!profileData) return;
    setAcceptedAgeRanges(_.cloneDeep(_.get(profileData, "accepted_age_range", [])));
    setAcceptNewPatients(profileData.accept_new_patients);
  }, [profileData]);

  const insuranceSelect = useMemo(
    () => (data: Parameters<typeof adaptProviderInsuranceOptions>[0]) =>
      adaptProviderInsuranceOptions(data, profileData?.accepted_insurance ?? []),
    [profileData?.accepted_insurance],
  );

  const { data: insuranceOptions } = useRawInsuranceList({
    enabled: !!profileData,
    select: insuranceSelect,
  });

  // Sync insurance options into local state whenever they resolve
  useEffect(() => {
    if (!insuranceOptions || !profileData) return;
    const selected = profileData.accepted_insurance
      .map((ai) => insuranceOptions.find((opt) => opt.value === ai))
      .filter((opt): opt is SelectOption<InsuranceCarrier> => opt !== undefined);

    setAcceptedInsuranceList(selected);
    setTempAcceptedInsuranceList(selected);
  }, [insuranceOptions]); // oxlint-disable-line react-hooks/exhaustive-deps

  // Navbar config
  useEffect(() => {
    dispatch(setDynamicRouteConfiguration({ logoSection: { title: "My Profile" } }));

    return () => dispatch(setDynamicRouteConfiguration());
  }, [dispatch]);

  // Hash-scroll on load
  useEffect(() => {
    if (!profileData || hasScrolledOnLoad) return;
    const hash = window.location.hash;

    if (!hash) return;
    const element = document.querySelector(hash);

    if (!element) return;
    window.scrollTo({
      behavior: "smooth",
      top: element.getBoundingClientRect().top - document.body.getBoundingClientRect().top - 85,
    });
    setHasScrolledOnLoad(true);
  }, [profileData, hasScrolledOnLoad]);

  const toggleStateModal = () => setStateModal((prev) => !prev);
  const toggleEditBasicInfo = () => setEditBasic((prev) => !prev);
  const showInsPopUp = () => setShowInsurancePopUp((prev) => !prev);

  const formatMobileNumber = (val: string | null | undefined): string => {
    if (!val) return "";
    const first = val.substring(0, 3);
    const middle = val.substring(3, 6);
    const last = val.substring(6, 10);

    return `(${first}) ${middle}-${last}`;
  };

  const iterableBasicInfo = (): { title: string; value: string }[] => {
    if (!profileData) return [];

    return [
      { title: "Full Name", value: profileData.name || "" },
      { title: "Provider Type", value: profileData.provider_type },
      { title: "Email", value: profileData.email || "" },
      { title: "Mobile", value: formatMobileNumber(profileData.mobile) },
      { title: "CP Credential", value: profileData.cp_credentials || "" },
      { title: "NPI Number", value: profileData.npi_number || "" },
      { title: "Practice", value: profileData.practice || "" },
    ];
  };

  const renderFormElement = (
    formEl: SchemaItem,
    formikProps: FormikProps<ProviderProfileData>,
  ): JSX.Element => {
    if (formEl.name === "provider_type" || formEl.name === "email" || formEl.name === "practice") {
      return (
        <FormikInput
          disabled
          errors={formikProps.errors}
          formEl={formEl}
          inputBorderDisabled="border-0 px-0"
          testId={`ProviderBasicInfoFormField_${formEl.name}`}
          touched={formikProps.touched}
        />
      );
    } else if (formEl.name === "mobile") {
      return (
        <FormikMobileInput
          errors={formikProps.errors}
          formEl={formEl}
          onChange={async (val: { value: string }) => {
            await formikProps.setFieldValue("mobile", val.value);
            formikProps.setFieldTouched("mobile");
          }}
          testId={`ProviderBasicInfoFormField_${formEl.name}`}
          touched={formikProps.touched}
          value={formikProps && formikProps.values.mobile}
        />
      );
    } else if (isUserAdmin()) {
      return (
        <FormikInput
          errors={formikProps.errors}
          formEl={formEl}
          testId={`ProviderBasicInfoFormField_${formEl.name}`}
          touched={formikProps.touched}
        />
      );
    }

    return (
      <FormikInput
        disabled
        errors={formikProps.errors}
        formEl={formEl}
        inputBorderDisabled="border-0 px-0"
        testId={`ProviderBasicInfoFormField_${formEl.name}`}
        touched={formikProps.touched}
      />
    );
  };

  const submitHandler = (
    formData: ProviderProfileData,
    actions: FormikHelpers<ProviderProfileData>,
  ): void => {
    actions.setSubmitting(false);
    const data = { ...formData };
    const userData = {
      mobile: data["mobile"],
      ...(isUserAdmin() && {
        cp_credentials: data["cp_credentials"],
        name: data["name"],
        npi_number: data["npi_number"],
      }),
    };

    mutateAsync({ body: userData, providerId: providerId ?? undefined });
    toggleEditBasicInfo();
  };

  const renderForm = (formikProps: FormikProps<ProviderProfileData>): JSX.Element => (
    <Form>
      <div className="mx-auto px-0">
        <div className="container px-0">
          <div className="row">
            {PROVIDER_BASIC_SCHEMA.map((formEl) => (
              <div className="col-12 col-lg-6" key={formEl.name}>
                {renderFormElement(formEl, formikProps)}
              </div>
            ))}
          </div>

          {!isUserAdmin() && (
            <div>
              <p className="ProviderProfile__toUpdateMessage">
                To update read-only fields, contact{" "}
                <a href="mailto:support@aptihealth.com">support@aptihealth.com</a>.
              </p>
            </div>
          )}
        </div>
      </div>

      <SubmitControls cancelHandler={toggleEditBasicInfo} testId="ProviderBasicInfoForm" />
    </Form>
  );

  const acceptNewPatientsChangeHandler = (isSelected: boolean): void => {
    setAcceptNewPatients(isSelected); // optimistic
    mutateAsync({ body: { accept_new_patients: isSelected }, providerId: providerId ?? undefined });
  };

  const handleInputChange = (_selection: unknown, action: SelectActionMeta): void => {
    if (!action) return;
    let updated: SelectOption<InsuranceCarrier>[];

    switch (action.action) {
      case "clear":
        updated = [];
        break;

      case "remove-value":
        updated = tempAcceptedInsuranceList.filter(
          (val) => val.value !== action.removedValue.value,
        );
        break;

      case "select-option":
        updated = action.option
          ? [...tempAcceptedInsuranceList, action.option]
          : tempAcceptedInsuranceList;
        break;

      default:
        return;
    }
    setTempAcceptedInsuranceList(updated);
  };

  const confirmInsuranceUpdate = (): void => {
    const newInsurance = tempAcceptedInsuranceList.map((i) => i.value);

    mutateAsync({
      body: { accepted_insurance: newInsurance },
      providerId: providerId ?? undefined,
    });
    showInsPopUp();
    setAcceptedInsuranceList([...tempAcceptedInsuranceList]);
  };

  const commitAndSubmitStateLicensureDataToServer = (
    updatedStatesToPractice: StateToPractice[],
  ): void => {
    const states_to_practice = updatedStatesToPractice.map(
      ({ expirationDate, licenseNumber, state }) => ({ expirationDate, licenseNumber, state }),
    );

    mutateAsync({ body: { states_to_practice }, providerId: providerId ?? undefined });
    toggleStateModal();
  };

  const editWorkingHours = (): void => {
    let url = providerRoute + "/profile/hours";

    if (providerId) url = `${url}/${providerId}`;
    history.push(url);
  };

  const safeGetStatesOfPractice = (): StateToPractice[] => {
    if (_.isNil(profileData?.states_to_practice) || _.isEmpty(profileData?.states_to_practice)) {
      return [];
    }

    return profileData!.states_to_practice;
  };

  const shouldAcceptedInsuranceBeDisabled = (): boolean =>
    ["CASE_MANAGER", "PEER"].includes(profileData?.provider_type ?? "");

  const shouldDisplayCalendarPreferences = (): boolean => {
    if (!profileData?.provider_type) return false;

    return !([ProviderEnum.CARE_MANAGER, ProviderEnum.PCP] as string[]).includes(
      profileData.provider_type,
    );
  };

  const handleSetAcceptedAgeRanges = (ageRanges: AgeRange[]): void => {
    setAcceptedAgeRanges(ageRanges); // optimistic
    mutateAsync({ body: { accepted_age_range: ageRanges }, providerId: providerId ?? undefined });
  };

  const renderEachWorkingHourDetail = (weekDay: string): JSX.Element => {
    const dayData = (
      profileData?.availability as
        | Partial<Record<string, { enabled: boolean; slots: [string, string][] }>>
        | undefined
    )?.[weekDay];
    const localeTime =
      dayData && dayData.slots.map((pair) => pair.map((t) => dayjs(t, "HH:mm").format("hh:mm a")));

    return (
      <li {...getAutomationDataAttr(`WorkingHours${weekDay}`)}>
        <strong className="tw:text-sm tw:font-bold">{weekDay}</strong>

        <div className="fs-14 d-flex">
          {dayData &&
            localeTime.map((hrs, index) => (
              <span className="w-50" key={index}>
                {hrs.join(" to ")}
              </span>
            ))}

          {dayData !== undefined
            ? dayData.slots.length < 1 && "No Slots Defined"
            : "No Slots Defined"}
        </div>
      </li>
    );
  };

  const showProviderTypeSection =
    profileData?.provider_type?.startsWith("BEHAVIORAL") ||
    profileData?.provider_type === "PRESCRIBE" ||
    isAuthorized(provider_case_manager) ||
    isAuthorized(provider_peer);

  const updateStatesToPracticeProps = {
    commitAndSubmitStateLicensureDataToServer,
    isEditable: true,
    showUpdateStateModal: stateModal,
    states_to_practice: safeGetStatesOfPractice(),
    toggleStateModal,
  };

  return (
    <>
      {showInsurancePopUp && (
        <div
          className="insurancePopUp position-fixed d-flex align-items-center tw:justify-center"
          data-public="true"
        >
          <div className="insurancePopUp__inner d-flex flex-column text-center position-absolute">
            <h2 className="fs-17 mt-4 tw:font-bold">Accepted Insurance</h2>
            <span className="fs-15 mt-2">Accepted Insurance Types</span>

            <div style={{ margin: "0 auto", width: "80%" }}>
              {insuranceOptions && (
                <Select
                  className="basic-multi-select"
                  classNamePrefix="select"
                  closeMenuOnSelect={true}
                  defaultValue={acceptedInsuranceList}
                  isMulti
                  options={insuranceOptions}
                  {...getAutomationDataAttr("AcceptedInsuranceSelect", "id")}
                  onChange={handleInputChange}
                />
              )}
            </div>

            <div className="insurancePopUp__btn-wpr d-flex pt-3 my-4 tw:justify-center">
              <Button className="Btn Btn--otl-pri Btn-sm mx-2" onClick={showInsPopUp} type="button">
                Cancel
              </Button>

              <Button
                className="Btn Btn--pri Btn-sm mx-2"
                onClick={confirmInsuranceUpdate}
                type="button"
                {...getAutomationDataAttr("UpdateAcceptedInsurance")}
              >
                Update
              </Button>
            </div>
          </div>
        </div>
      )}

      <MyProfile
        asideContent={<ProfileSidebar profileData={profileData} />}
        isLoading={isLoading || !profileData}
        renderMainContent={() => (
          <>
            {showProviderTypeSection && (
              <AcceptNewPatientsCard
                isAcceptingNewPatients={acceptNewPatients}
                isDisabled={isAuthorized(provider_case_manager) || isAuthorized(provider_peer)}
                onChange={acceptNewPatientsChangeHandler}
              />
            )}

            <UniversalAccessManagementCard providerDetails={profileData} />

            {([ProviderEnum.BEHAVIORAL, ProviderEnum.BEHAVIORAL_INTAKE] as string[]).includes(
              profileData?.provider_type ?? "",
            ) && (
              <RequireCosignatureGate
                isDisabled={!isAuthorized(admin_cse)}
                providerName={profileData?.name}
                userId={profileData?.username}
              />
            )}

            <DashboardCard2
              Actions={
                !editBasic && (
                  <Button2
                    onPress={toggleEditBasicInfo}
                    size="sm"
                    variant="link"
                    {...getAutomationDataAttr("ProviderProfileBasicInfoEditButton")}
                  >
                    Edit
                  </Button2>
                )
              }
              title="Basic Information"
            >
              <div className="ProviderProfile__basic-info row no-gutters">
                {!editBasic &&
                  iterableBasicInfo().map((item, index) => (
                    <div className="col-12 col-lg-6" key={item.title || index}>
                      <ProfileField
                        title={item.title}
                        value={item.value}
                        {...getAutomationDataAttr(
                          `ProviderBasicInfo${item.title.replaceAll(" ", "")}`,
                        )}
                      />
                    </div>
                  ))}

                {editBasic && (
                  <CustomForm
                    initialValues={profileData}
                    onSubmit={submitHandler}
                    render={renderForm}
                    validationSchema={validationSchema}
                  />
                )}
              </div>
            </DashboardCard2>

            <StatesToPractice {...updateStatesToPracticeProps} />

            {showProviderTypeSection && (
              <AcceptedAgeRange
                acceptedAgeRanges={acceptedAgeRanges}
                setAcceptedAgeRanges={handleSetAcceptedAgeRanges}
              />
            )}

            <AcceptedInsuranceCard
              acceptedInsurance={profileData?.accepted_insurance}
              isAdmin={isUserAdmin()}
              isDisabled={shouldAcceptedInsuranceBeDisabled()}
              onEdit={showInsPopUp}
            />

            {shouldDisplayCalendarPreferences() && (
              <CalendarPreferencesCard providerId={(providerId ?? undefined) as UUID | undefined} />
            )}

            <DashboardCard2
              Actions={
                !(isAuthorized(provider_case_manager) || isAuthorized(provider_peer)) && (
                  <Button2
                    onPress={editWorkingHours}
                    size="sm"
                    variant="link"
                    {...getAutomationDataAttr("WorkingHoursEditButton")}
                  >
                    Edit
                  </Button2>
                )
              }
              // case/peer managers cannot edit hours
              title="Working Hours"
            >
              <div className="ProviderProfile__working-hours" data-public="true">
                {profileData?.availability ? (
                  <ul className="tw:flex tw:flex-col tw:gap-4">
                    {renderEachWorkingHourDetail("Monday")}
                    {renderEachWorkingHourDetail("Tuesday")}
                    {renderEachWorkingHourDetail("Wednesday")}
                    {renderEachWorkingHourDetail("Thursday")}
                    {renderEachWorkingHourDetail("Friday")}
                    {renderEachWorkingHourDetail("Saturday")}
                    {renderEachWorkingHourDetail("Sunday")}
                  </ul>
                ) : (
                  "No Working Hours Defined"
                )}
              </div>
            </DashboardCard2>
          </>
        )}
      />
    </>
  );
}

// Preserved for WorkingHours.js compatibility — migrate when WorkingHours is refactored
export const getProfile = async (
  that: { setState: (state: Record<string, unknown>) => void },
  providerId: string,
): Promise<void> => {
  try {
    const response = await providerRequests.fetch_provider_profile({
      context: { showLoader: false },
      searchParams: { providerId },
    });
    const profileData = (response as { user: ProviderProfileData } | undefined)?.user;

    if (profileData && !Object.hasOwn(profileData, "availability")) {
      (profileData as Record<string, unknown>)["availability"] = {};
    }
    that.setState({
      acceptedAgeRanges: _.cloneDeep(_.get(profileData, "accepted_age_range", [])),
      acceptNewPatients: profileData.accept_new_patients,
      profileData,
    });
  } catch (err) {
    console.log(err);
  }
};
